import { Flex, Link, Text } from '@chakra-ui/react'
import React, { useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { MangoButton } from '~/components/shared/MangoButton'
import { useAnalytics } from '~/hooks/useAnalytics'
import useCloudwatchLogger from '~/hooks/useCloudwatchLogger'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'

import { useCallErrorStyle } from './componentStyles/CallError.style'

// First auto-retry delay. 2s is a deliberate balance: long enough that most
// transient connect-window blips clear the timer before it fires, short enough
// that a real outage recovers quickly. It does NOT need to outlast a full
// reconnect — retry goes through resetConnection -> hardResetSipClient, which
// is single-flight guarded, so a retry landing mid-reconnect is coalesced
// (dropped), not re-entered as churn. The manual Reconnect button is instant.
export const FIRST_RETRY_DELAY_MS = 2000
export const RETRY_INTERVAL_MS = 5000

interface CallErrorViewProps {
  error: Error | null
  isMicPermissionDenied: boolean
  isConnected: boolean
  onRetryClick: () => void
}
export const CallErrorView = ({
  isMicPermissionDenied,
  onRetryClick,
  isConnected,
}: CallErrorViewProps) => {
  const { t } = useTranslation()
  const { logger } = useCloudwatchLogger('CallErrorView')
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)

  // Track only the manual button press. The auto-retry timer used to fire the
  // same handler, so a handful of clients stuck in a reconnect loop flooded
  // analytics with thousands of "click" events that no human ever made.
  const handleRetryClick = () => {
    onRetryClick()
    track(WebPhoneAnalyticsEvents.RestartSipClientErrorClick)
  }

  // Keep the retry timer pointed at the latest retry callback without making
  // the timer effect depend on (and restart on) its identity. `onRetryClick`
  // is not memoized — so a useCallback wouldn't be stable and adding it to
  // deps would restart the timer every render. Updating the ref in an effect
  // (not during render) avoids the render-phase write while staying current.
  const onRetryClickRef = useRef(onRetryClick)
  useEffect(() => {
    onRetryClickRef.current = onRetryClick
  })

  useEffect(() => {
    let retryTimer: NodeJS.Timeout | null = null

    const attemptReconnect = () => {
      if (!isConnected) {
        logger?.info('Retrying connection...')
        onRetryClickRef.current()
        retryTimer = setTimeout(attemptReconnect, RETRY_INTERVAL_MS)
      }
    }

    // Defer the first auto-attempt: transient connect-window blips settle
    // within this window and clear the timer, avoiding spurious reconnects.
    // The manual Reconnect button still retries instantly.
    if (!isConnected) {
      retryTimer = setTimeout(attemptReconnect, FIRST_RETRY_DELAY_MS)
    }

    return () => {
      if (retryTimer) {
        clearTimeout(retryTimer)
      }
    }
  }, [isConnected, logger])
  const styles = useCallErrorStyle()
  return (
    <Flex css={styles.container}>
      {isMicPermissionDenied ? (
        <Flex css={styles.micBody}>
          <Text css={{ ...styles.errorText, ...styles.h6 }}>
            {t('webPhone.callError.microphoneDisabled')}
          </Text>
          <Text css={{ ...styles.errorText, ...styles.body1 }}>
            <span style={{ display: 'block' }}>
              {t('webPhone.callError.microphoneEnableText')}
            </span>
            <span style={{ display: 'block' }}>
              <Trans
                i18nKey="webPhone.callError.seeHereForHelp"
                components={{
                  link: (
                    <Link
                      css={styles.link}
                      href="https://support.google.com/chrome/answer/2693767?hl=en&co=GENIE.Platform%3DDesktop"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                }}
              />
            </span>
          </Text>
        </Flex>
      ) : !isConnected ? (
        <>
          <Flex css={styles.header}>
            <Text css={styles.disconnectedTitle}>
              {t('webPhone.callError.youAreDisconnected')}
            </Text>
          </Flex>
          <Flex css={styles.errorBody}>
            <Text css={{ ...styles.errorText, ...styles.body1 }}>
              <span style={{ display: 'block' }}>
                {t('webPhone.callError.connectionInterrupted')}
              </span>
            </Text>
            <MangoButton onClick={handleRetryClick}>
              {t('webPhone.callError.reconnect')}
            </MangoButton>
          </Flex>
        </>
      ) : null}
    </Flex>
  )
}
